import { Link, useLocation } from "@tanstack/react-router";
import { ExternalLink, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import type { SmRole } from "@/domain/sm-models";
import type { BusinessProfile } from "@/lib/market-catalog";
import { ReleaseInfoFooter } from "@/components/platform/ReleaseInfoFooter";
import { TenantPublicStatusSwitch } from "@/components/TenantPublicStatusSwitch";
import { getTenantBaseDomain } from "@/lib/tenant-dns";
import { useMerchantNavItems } from "@/components/ds/use-merchant-nav-items";
import { SidebarNav } from "@/components/ds/SidebarNav";
import { LocationContextSwitcher } from "@/components/LocationContextSwitcher";

export interface SidebarProps {
  role: SmRole | undefined;
  tenantId?: string;
  tenantSlug: string;
  planId?: string | null;
  businessProfile?: BusinessProfile;
  operationalRoleId?: string | null;
  showSetupBanner?: boolean;
  onNavigate?: () => void;
  /** SCRUM-252: estado colapsado del sidebar desktop (persistido en localStorage). */
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

/**
 * Sidebar del Design System (F1/F2/F3) — misma lógica de navegación/permisos que
 * DashboardSidebarContent (navItemsForRole, fetchProfileNavConfig, gating por plan),
 * nueva presentación visual. Primer consumidor: /app/dashboard.
 */
export function Sidebar({
  role,
  tenantId,
  tenantSlug,
  planId,
  businessProfile,
  operationalRoleId,
  showSetupBanner,
  onNavigate,
  collapsed = false,
  onToggleCollapse,
}: SidebarProps) {
  const location = useLocation();
  const navItems = useMerchantNavItems({
    role,
    planId,
    businessProfile,
    operationalRoleId,
  });
  const storefrontHref = tenantSlug ? `https://${tenantSlug}.${getTenantBaseDomain()}` : null;

  return (
    <>
      <div className="mb-1.5 flex shrink-0 items-center justify-between gap-2 px-3 pt-0.5">
        {!collapsed ? (
          <h1 className="min-w-0 text-base font-bold tracking-tight">
            <span className="text-primary">Structura</span>{" "}
            <span className="font-light text-foreground">Market</span>
          </h1>
        ) : null}
        {!collapsed && tenantId && role !== "staff" ? (
          <TenantPublicStatusSwitch tenantId={tenantId} compact className="w-auto shrink-0" />
        ) : null}
        {onToggleCollapse ? (
          <SidebarCollapseToggle collapsed={collapsed} onToggle={onToggleCollapse} className={collapsed ? "mx-auto" : undefined} />
        ) : null}
      </div>

      {!collapsed ? (
        <div className="mb-1.5 shrink-0 px-3">
          <LocationContextSwitcher />
        </div>
      ) : null}

      <SidebarNav items={navItems} pathname={location.pathname} onNavigate={onNavigate} collapsed={collapsed} />

      {showSetupBanner && !collapsed && (
        <div className="shrink-0 border-t border-sidebar-border px-3 py-2">
          <Link
            to="/app/onboarding"
            onClick={() => onNavigate?.()}
            className="block rounded-md border border-warning/30 bg-warning/10 px-2.5 py-1.5 text-[11px] text-warning hover:bg-warning/15"
          >
            Setup incompleto →
          </Link>
        </div>
      )}

      <div className={cn("mt-auto shrink-0 border-t border-sidebar-border py-1.5", collapsed ? "flex justify-center px-1" : "px-3")}>
        {collapsed ? (
          <ThemeToggle />
        ) : (
          <>
            <div className="mb-0.5 hidden items-center justify-between lg:flex">
              <span className="text-[11px] text-muted-foreground">Tema</span>
              <ThemeToggle />
            </div>
            <div className="mb-0.5 hidden lg:block">
              <ReleaseInfoFooter productLabel="Structura Market" compact />
            </div>
            {storefrontHref ? (
              <a
                href={storefrontHref}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => onNavigate?.()}
                className="flex items-center gap-1.5 rounded-md px-0.5 py-0.5 text-[11px] font-medium text-muted-foreground transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                <ExternalLink className="h-3 w-3 shrink-0" aria-hidden />
                Ver tienda pública
              </a>
            ) : null}
          </>
        )}
      </div>
    </>
  );
}

/** Toggle expandir/colapsar sidebar (SCRUM-252) — icono + tooltip, sin diálogo nativo. */
export function SidebarCollapseToggle({
  collapsed,
  onToggle,
  className,
}: {
  collapsed: boolean;
  onToggle: () => void;
  className?: string;
}) {
  const label = collapsed ? "Expandir menú" : "Colapsar menú";
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className={cn("h-7 w-7 shrink-0 text-muted-foreground hover:text-foreground", className)}
            onClick={onToggle}
            aria-label={label}
            aria-pressed={collapsed}
          >
            {collapsed ? <PanelLeftOpen className="h-4 w-4" aria-hidden /> : <PanelLeftClose className="h-4 w-4" aria-hidden />}
          </Button>
        </TooltipTrigger>
        <TooltipContent side="right">{label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
